import {
  Product3DAssetBounds,
  Product3DAssetCompression,
  Product3DDimensions,
  Product3DAssetMaterialSlot,
  Product3DAssetQuality,
} from "../../products/product3DTypes";
import {
  PRIMITIVE_SYMBOL_3D_DEFAULTS,
  PrimitiveSymbol3DModelKey,
  getPrimitiveSymbol3DDefaults,
} from "../registry/primitiveSymbol3DConfig";
import { STANDARD_SYMBOL_3D_ASSET_OVERRIDES } from "./standardSymbol3DAssetOverrides.generated";

export const STANDARD_SYMBOL_3D_ASSET_VERSION = "v1";
export const STANDARD_SYMBOL_3D_ASSET_BASE_URL = "/models/standard-symbols/v1";

export type StandardSymbol3DAssetStatus = "ready" | "pending";

export type StandardSymbol3DAssetSource =
  | "ai-photo"
  | "catalog-owned"
  | "cc0"
  | "manual"
  | "placeholder";

export type StandardSymbol3DAssetLicense = "catalog-owned" | "cc0" | "unknown";

export type StandardSymbol3DAsset = {
  modelKey: PrimitiveSymbol3DModelKey;
  status: StandardSymbol3DAssetStatus;
  version: typeof STANDARD_SYMBOL_3D_ASSET_VERSION;
  url?: string;
  dimensions: Product3DDimensions;
  bounds: Product3DAssetBounds;
  triangleCount?: number;
  fileBytes?: number;
  textureMaxSize?: number;
  source: StandardSymbol3DAssetSource;
  license: StandardSymbol3DAssetLicense;
  compression: Product3DAssetCompression;
  quality?: Product3DAssetQuality;
  materialSlots?: Product3DAssetMaterialSlot[];
};

export type ReadyStandardSymbol3DAsset = StandardSymbol3DAsset & {
  status: "ready";
  url: string;
};

export const STANDARD_SYMBOL_3D_ASSETS: Record<
  PrimitiveSymbol3DModelKey,
  StandardSymbol3DAsset
> = {
  ...(Object.fromEntries(
    Object.keys(PRIMITIVE_SYMBOL_3D_DEFAULTS).map((modelKey) => [
      modelKey,
      createPendingStandardAsset(modelKey as PrimitiveSymbol3DModelKey),
    ]),
  ) as Record<PrimitiveSymbol3DModelKey, StandardSymbol3DAsset>),
  ...STANDARD_SYMBOL_3D_ASSET_OVERRIDES,
};

export function getStandardSymbol3DAsset(
  modelKey: PrimitiveSymbol3DModelKey | undefined,
  assets: Record<
    PrimitiveSymbol3DModelKey,
    StandardSymbol3DAsset
  > = STANDARD_SYMBOL_3D_ASSETS,
): StandardSymbol3DAsset | undefined {
  return modelKey ? assets[modelKey] : undefined;
}

export function getReadyStandardSymbol3DAsset(
  modelKey: PrimitiveSymbol3DModelKey | undefined,
  assets: Record<
    PrimitiveSymbol3DModelKey,
    StandardSymbol3DAsset
  > = STANDARD_SYMBOL_3D_ASSETS,
): ReadyStandardSymbol3DAsset | undefined {
  const asset = getStandardSymbol3DAsset(modelKey, assets);
  return isReadyStandardSymbol3DAsset(asset) ? asset : undefined;
}

export function isReadyStandardSymbol3DAsset(
  asset: StandardSymbol3DAsset | undefined,
): asset is ReadyStandardSymbol3DAsset {
  return asset?.status === "ready" && Boolean(asset.url);
}

export function getStandardSymbol3DAssetSignature(): string {
  return Object.values(STANDARD_SYMBOL_3D_ASSETS)
    .map((asset) =>
      [
        asset.modelKey,
        asset.status,
        asset.version,
        asset.url ?? "",
        asset.fileBytes ?? "",
        asset.triangleCount ?? "",
      ].join("="),
    )
    .join("|");
}

export function getStandardSymbol3DAssetSlug(
  modelKey: PrimitiveSymbol3DModelKey,
): string {
  return modelKey.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "");
}

function createPendingStandardAsset(
  modelKey: PrimitiveSymbol3DModelKey,
): StandardSymbol3DAsset {
  const defaults = getPrimitiveSymbol3DDefaults(modelKey);
  const dimensions = defaults?.dimensions ?? {};
  return {
    modelKey,
    status: "pending",
    version: STANDARD_SYMBOL_3D_ASSET_VERSION,
    dimensions,
    bounds: {
      min: [-(dimensions.width ?? 0) / 2, -(dimensions.depth ?? 0) / 2, 0],
      max: [
        (dimensions.width ?? 0) / 2,
        (dimensions.depth ?? 0) / 2,
        dimensions.height ?? 0,
      ],
      center: [0, 0, (dimensions.height ?? 0) / 2],
      size: dimensions,
    },
    source: "placeholder",
    license: "unknown",
    compression: {
      geometry: "meshopt",
      textures: "webp",
    },
  };
}
